import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Account } from '../models/account';
import { Address } from '../models/address';
import { cryptoPrimary, cryptoSecondary } from '../theme/colors';
import findImage from '../content/images';
import AddressText from './AddressText';
import { useContext, useMemo, useState } from 'react';
import { PaymentDataContext } from '../context/PaymentData';

interface Props {
  account: Account;
  address: Address;
}

export default function AssetItemUTXO({ account, address }: Props) {
  const paymentDataCtx = useContext(PaymentDataContext);

  const dataImage = useMemo(() => {
    const icon = paymentDataCtx.paymentData?.walletList.find(
      (asset) => asset.symbol === account.symbol
    )?.icon;
    if (icon) {
      return icon;
    } else {
      return findImage(account.symbol);
    }
  }, [account, paymentDataCtx.paymentData]);

  let USDollar = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currencyDisplay: 'narrowSymbol',
    currency: account.fiat_currency || 'USD'
  });
  const [symbolRate, ...othersRate] = USDollar.formatToParts(account.rate);
  const rateString = `${symbolRate.value} ${othersRate
    .map((v) => v.value)
    .join('')}`;
  const [symbolBallance, ...othersBallance] = USDollar.formatToParts(
    account.fiat_balance
  );
  const fiatBalanceString = `${symbolBallance.value} ${othersBallance
    .map((v) => v.value)
    .join('')}`;
  let numberFormat = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    maximumFractionDigits: 9
  });
  const decimalBalance = numberFormat.format(
    Math.trunc(address.accounts[0].decimal_balance * 100000000) / 100000000
  );

  const [copied, setCopied] = useState(false);

  const handleCopyClick = () => {
    navigator.clipboard.writeText(address.address);
    setCopied(true);
  };

  return (
    <Box
      component={'div'}
      sx={{
        height: '120px',
        padding: '1px',
        paddingTop: 0,
        backgroundColor: cryptoSecondary[50]
      }}
    >
      <Stack
        sx={{
          justifyContent: 'space-between',
          height: '120px',
          py: '10px',
          px: '15px',
          backgroundColor: cryptoPrimary[50],
          color: 'black'
        }}
      >
        <Stack direction={'row'} sx={{ justifyContent: 'space-between' }}>
          <Typography sx={{ fontSize: '0.875rem' }}>
            {account.currency}
          </Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'rgba(0,0,0,0.3)' }}>
            {address.hd_path}
          </Typography>
        </Stack>
        <Stack direction={'row'} sx={{ justifyContent: 'space-between' }}>
          <Stack
            direction={'row'}
            sx={{ justifyContent: 'flex-start', alignItems: 'center' }}
          >
            <IconButton disabled disableRipple sx={{ p: 0, borderRadius: 0 }}>
              <img
                style={{
                  width: 35
                }}
                src={dataImage}
                alt={account.symbol}
              />
            </IconButton>
            <Stack sx={{ alignItems: 'flex-start', px: '15px' }}>
              <Stack direction={'row'}>
                <Typography sx={{ fontSize: '1.125rem' }}>
                  {account.symbol}
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: '0.875rem' }}>
                {rateString}
              </Typography>
            </Stack>
          </Stack>
          <Stack sx={{ alignItems: 'flex-end' }}>
            <Typography sx={{ fontSize: '1.125rem' }}>
              {decimalBalance}
            </Typography>
            <Typography sx={{ fontSize: '0.875rem' }}>
              {fiatBalanceString}
            </Typography>
          </Stack>
        </Stack>
        <Tooltip title={copied ? 'Copied' : 'Click to copy'}>
          <Stack
            direction={'row'}
            sx={{
              width: '100%',
              justifyContent: 'flex-start',
              alignItems: 'center',
              mb: '5px',
              cursor: 'pointer'
            }}
            onClick={handleCopyClick}
          >
            <AddressText
              address={address.address}
              fontHeight="0.875rem"
              lineHeight="1rem"
              stringLen={320}
              color={cryptoPrimary[200]}
            />
          </Stack>
        </Tooltip>
      </Stack>
    </Box>
  );
}
